"use client";

import { c as _c } from "react/compiler-runtime";
import { OrganizationWalletBadge } from "@/components/ui/OrganizationWalletBadge";
import { findMatchingOrganizationWallet } from "@/components/ui/workflow-step-row/components/InlineAddressLink";
import { useB3Profile } from "@/hooks/useB3Profile";
import { formatUsername } from "@/lib/utils";
import { isSolanaAddress } from "@/lib/utils/address";
import { isAddress } from "viem";
import UserAvatar from "@/app/(v2)/_components/UserAvatar";
import { isHexLikeValue, truncateHexValue } from "./ActionNodeValueFormat";
import { useOrgWalletsContext } from "./OrganizationWalletsContext";
interface AddressDisplayProps {
  address: string;
  label?: string;
  size?: "sm" | "md";
  showLabel?: boolean;
  showWalletAddress?: boolean;
}

/**
 * AddressDisplay - Shows an address with ENS avatar and name resolution
 * Used in workflow nodes (ActionNode, TriggerNode) and workflow-step-row
 * Only shows avatar if the address has a profile avatar, otherwise just shows truncated address
 */
export function AddressDisplay(t0) {
  const $ = _c(60);
  const {
    address,
    label,
    size: t1,
    showLabel: t2,
    showWalletAddress: t3
  } = t0;
  const size = t1 === undefined ? "sm" : t1;
  const showLabel = t2 === undefined ? true : t2;
  const showWalletAddress = t3 === undefined ? true : t3;
  const {
    wallets
  } = useOrgWalletsContext();
  let t4;
  if ($[0] !== address) {
    t4 = address && address.startsWith("0x") && address.length === 42 && isAddress(address);
    $[0] = address;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const isEvmAddress = t4;
  let t5;
  if ($[2] !== address || $[3] !== isEvmAddress) {
    t5 = isEvmAddress || !!address && isSolanaAddress(address);
    $[2] = address;
    $[3] = isEvmAddress;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const isValidAddress = t5;
  let t6;
  if ($[5] !== address) {
    t6 = isHexLikeValue(address) ? truncateHexValue(address) : address;
    $[5] = address;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const fallbackDisplay = t6;
  let t7;
  if ($[7] !== address || $[8] !== isValidAddress || $[9] !== wallets) {
    t7 = isValidAddress ? findMatchingOrganizationWallet(address, wallets) : undefined;
    $[7] = address;
    $[8] = isValidAddress;
    $[9] = wallets;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const matchingWallet = t7;
  const t8 = !!isEvmAddress && !matchingWallet;
  let t9;
  if ($[11] !== address || $[12] !== t8) {
    t9 = {
      addressOrName: address,
      enabled: t8
    };
    $[11] = address;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const {
    data: profile
  } = useB3Profile(t9);
  const avatarSize = size === "sm" ? 16 : 20;
  const textSizeClass = size === "sm" ? "text-xs" : "text-sm";
  const hasAvatar = !!profile?.avatar;
  let t10;
  if ($[14] !== address || $[15] !== profile?.displayName || $[16] !== profile?.name) {
    t10 = formatUsername(profile?.displayName || profile?.name) || `${address.slice(0, 6)}...${address.slice(-4)}`;
    $[14] = address;
    $[15] = profile?.displayName;
    $[16] = profile?.name;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const displayName = t10;
  if (!isValidAddress) {
    const t11 = `flex items-start gap-2 overflow-hidden ${textSizeClass}`;
    let t12;
    if ($[18] !== label || $[19] !== showLabel) {
      t12 = showLabel && label && <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium capitalize">{label}:</span>;
      $[18] = label;
      $[19] = showLabel;
      $[20] = t12;
    } else {
      t12 = $[20];
    }
    let t13;
    if ($[21] !== address || $[22] !== fallbackDisplay) {
      t13 = <span className="text-ds-text-secondary min-w-0 truncate font-mono" title={address}>{fallbackDisplay}</span>;
      $[21] = address;
      $[22] = fallbackDisplay;
      $[23] = t13;
    } else {
      t13 = $[23];
    }
    let t14;
    if ($[24] !== t11 || $[25] !== t12 || $[26] !== t13) {
      t14 = <div className={t11}>{t12}{t13}</div>;
      $[24] = t11;
      $[25] = t12;
      $[26] = t13;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    return t14;
  }
  if (matchingWallet) {
    const t11 = `flex items-center gap-2 overflow-hidden ${textSizeClass}`;
    let t12;
    if ($[28] !== label || $[29] !== showLabel) {
      t12 = showLabel && label && <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium capitalize">{label}:</span>;
      $[28] = label;
      $[29] = showLabel;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    let t13;
    if ($[31] !== matchingWallet || $[32] !== showWalletAddress) {
      t13 = <OrganizationWalletBadge wallet={matchingWallet} variant="summary" showAddress={showWalletAddress} />;
      $[31] = matchingWallet;
      $[32] = showWalletAddress;
      $[33] = t13;
    } else {
      t13 = $[33];
    }
    let t14;
    if ($[34] !== t11 || $[35] !== t12 || $[36] !== t13) {
      t14 = <div className={t11}>{t12}{t13}</div>;
      $[34] = t11;
      $[35] = t12;
      $[36] = t13;
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    return t14;
  }
  if (hasAvatar) {
    const t11 = `flex items-center gap-2 overflow-hidden ${textSizeClass}`;
    let t12;
    if ($[38] !== label || $[39] !== showLabel) {
      t12 = showLabel && label && <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium capitalize">{label}:</span>;
      $[38] = label;
      $[39] = showLabel;
      $[40] = t12;
    } else {
      t12 = $[40];
    }
    const t13 = `font-medium text-ds-text-primary ${textSizeClass}`;
    let t14;
    if ($[41] !== address || $[42] !== avatarSize || $[43] !== t13) {
      t14 = <UserAvatar address={address} size={avatarSize} includeAvatar={true} includeUsername={true} textClassName={t13} imageClassName="flex-shrink-0" />;
      $[41] = address;
      $[42] = avatarSize;
      $[43] = t13;
      $[44] = t14;
    } else {
      t14 = $[44];
    }
    let t15;
    if ($[45] !== t11 || $[46] !== t12 || $[47] !== t14) {
      t15 = <div className={t11}>{t12}{t14}</div>;
      $[45] = t11;
      $[46] = t12;
      $[47] = t14;
      $[48] = t15;
    } else {
      t15 = $[48];
    }
    return t15;
  }
  const t11 = `flex items-center gap-2 overflow-hidden ${textSizeClass}`;
  let t12;
  if ($[49] !== label || $[50] !== showLabel) {
    t12 = showLabel && label && <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium capitalize">{label}:</span>;
    $[49] = label;
    $[50] = showLabel;
    $[51] = t12;
  } else {
    t12 = $[51];
  }
  const t13 = `text-ds-text-primary min-w-0 truncate font-medium ${textSizeClass}`;
  let t14;
  if ($[52] !== address || $[53] !== displayName || $[54] !== t13) {
    t14 = <span className={t13} title={address}>{displayName}</span>;
    $[52] = address;
    $[53] = displayName;
    $[54] = t13;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  let t15;
  if ($[56] !== t11 || $[57] !== t12 || $[58] !== t14) {
    t15 = <div className={t11}>{t12}{t14}</div>;
    $[56] = t11;
    $[57] = t12;
    $[58] = t14;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  return t15;
}
export { isAddressField } from "@/lib/utils/address";